import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getLifeAreaFeed } from "../../Services/explore";
import { t, LIFE_AREA_COPY } from "./copy";
import { resolveLifeArea, useLifeAreas } from "./lifeAreas";
import {
  AreaCardsSection,
  AreaHero,
  ExploreBackHeader,
  ExploreSubpageCard,
  TalkWithArisFooter,
  useStandaloneExploreHeader,
} from "./areaShared";
import "./style.css";

/**
 * Per-area feed — `GET /api/v4/explore/life-areas/{key}` (Figma 9139:19781,
 * "Dialogue Collection"). The payload is a `LifeAreaFeed`, whose sections are
 * keyed by `key` (HERO / RECOMMENDED_FOR_YOU / EXPLORE_MORE / TALK_WITH_ARIS)
 * — **not** the main feed's `ExploreSection.type` — so this page renders them
 * itself rather than through FeedSections. Content fields live under `cards`,
 * and RECOMMENDED_FOR_YOU is omitted server-side when empty rather than sent
 * empty, so each section renders exactly what arrives.
 *
 * Self-Knowledge is the one specialised area and never routes here — the
 * Explore tile sends SELF_KNOWLEDGE to /explore/self-knowledge instead.
 */

const SECTION_KEY = {
  HERO: "HERO",
  RECOMMENDED_FOR_YOU: "RECOMMENDED_FOR_YOU",
  EXPLORE_MORE: "EXPLORE_MORE",
  TALK_WITH_ARIS: "TALK_WITH_ARIS",
};

const byKey = (feed) => {
  const out = {};
  (feed?.sections || []).forEach((section) => {
    if (section?.key) out[section.key] = section;
  });
  return out;
};

function LifeArea() {
  useLifeAreas(); // re-render once the Life Areas arrive
  const { areaKey } = useParams();
  const navigate = useNavigate();

  const [feed, setFeed] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useStandaloneExploreHeader();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getLifeAreaFeed(areaKey);
        if (!cancelled) setFeed(data || null);
      } catch (e) {
        console.error("Failed to load the life area", e);
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [areaKey]);

  const area = resolveLifeArea({ key: feed?.display_area || areaKey });
  const sections = byKey(feed);
  const hero = sections[SECTION_KEY.HERO];
  const recommended = sections[SECTION_KEY.RECOMMENDED_FOR_YOU];
  const exploreMore = sections[SECTION_KEY.EXPLORE_MORE];
  const talk = sections[SECTION_KEY.TALK_WITH_ARIS];

  const openDialogue = (card) =>
    card?.dialogue_id ? navigate(`/explore/dialogue/${card.dialogue_id}`) : null;

  const noDialogues =
    !(recommended?.cards?.length > 0) && !(exploreMore?.cards?.length > 0);

  return (
    <div className="explore-page">
      <div className="explore-page__inner">
        <ExploreBackHeader to="/explore" />
        <ExploreSubpageCard>

          <AreaHero
            area={area}
            title={t(hero?.title_key, area.title)}
            description={t(hero?.description_key, area.description)}
          />

          {loading && <div className="explore-card explore-card--status">Loading…</div>}

          {!loading && error && (
            <div className="explore-card explore-card--status">
              We couldn&apos;t load this area. Please try again.
            </div>
          )}

          {!loading && !error && (
            <>
              <AreaCardsSection
                section={recommended}
                titleFallback="Recommended for You"
                onOpenDialogue={openDialogue}
              />

              <AreaCardsSection
                section={exploreMore}
                titleFallback="Explore More"
                onOpenDialogue={openDialogue}
              />

              {noDialogues && (
                <div className="explore-card explore-card--status">
                  {LIFE_AREA_COPY.empty}
                </div>
              )}

              <TalkWithArisFooter
                section={talk}
                onTalkWithAris={() => navigate("/dashboard")}
              />
            </>
          )}
        </ExploreSubpageCard>
      </div>
    </div>
  );
}

export default LifeArea;
